import { Button, ButtonMode, Popup, Tooltip, Icon, ICONS, ToasterMode } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React from 'react';
import useFetchUserPermissions from '../../../../hooks/permissions/useFetchUserPermissions';
import DocketAlertItem from './DocketAlertItem';
import styles from './DocketAlertPopup.module.scss';
import { AlertType, Preference, docketAlertOptions } from '../../../../models/DocketAlerts';
import { hasPermissionToDocketAlerts } from '../../../../utils/permissionsUtils';
import useUpdateDocketAlertPreferences from '../../../../hooks/docketAlerts/useUpdateDocketAlertPreference';
import { GENERIC_ERROR_TEXT } from '../../../../Base.constants';
import { useToast } from '../../../../hooks/toast/useToast';

const cx = classNames.bind(styles);

interface DocketAlertPopupProps {
  docketPreference: Preference;
  selectDocketAlert: (alertMode: AlertType) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  caseId: number;
  companyId: number;
}

const DocketAlertPopup = ({
  docketPreference,
  selectDocketAlert,
  isOpen,
  setIsOpen,
  caseId,
  companyId,
}: DocketAlertPopupProps): React.ReactElement => {
  const { data: userPermissions } = useFetchUserPermissions();
  const hasPermission = hasPermissionToDocketAlerts(userPermissions);
  const { showToast } = useToast();

  const { mutate: updatePreferences } = useUpdateDocketAlertPreferences();

  const bellIcon = docketPreference.mode !== AlertType.None ? ICONS.BellFull : ICONS.Bell;

  const handleOptionSelect = async (mode: AlertType) => {
    selectDocketAlert(mode);
    const preferencesToUpdate = {
      ...docketPreference,
      mode,
    };
    try {
      await updatePreferences({ preferences: preferencesToUpdate, caseId, companyId });
    } catch (error) {
      console.error('Error updating preferences', error);
      showToast(GENERIC_ERROR_TEXT, ToasterMode.ERROR);
    }
  };

  const isAnyAlertSelected = docketPreference.mode !== AlertType.None;

  const renderTriggerButton = () =>
    hasPermission && (
      <div className={cx('body')} data-testid="docket-alert-button">
        <Tooltip
          trigger={
            <Button
              dataTestId="docket-alert-button"
              mode={ButtonMode.SECONDARY}
              ariaLabel={isAnyAlertSelected ? 'Alerts On' : 'Alerts Off'}
              onClick={() => setIsOpen(!isOpen)}
            >
              <Icon name={bellIcon} />
              <Icon name={isOpen ? ICONS.CaretArrowUp : ICONS.CaretArrowDown} />
            </Button>
          }
          content={isAnyAlertSelected ? 'Alerts On' : 'Alerts Off'}
          disabled={isOpen}
        />
      </div>
    );

  return (
    <Popup
      className={cx('body')}
      trigger={renderTriggerButton()}
      on="click"
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      data-testid="docket-alert-popup"
    >
      <div className={cx('content')}>
        <DocketAlertItem
          title="Docket Alerts"
          options={docketAlertOptions}
          selectedOption={docketPreference.mode}
          onOptionSelect={handleOptionSelect}
          dataTestPrefix="docket-alert"
        />
      </div>
    </Popup>
  );
};

export default DocketAlertPopup;
